import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import NextLink from 'next/link';
import {boolean, object} from 'yup';

import {BirthSex} from '#apollo/schema';
import {Button, Checkbox, Form, Text} from '#component/base';
import {Link} from '#component/base';
import {ModalRoot, ModalTrigger} from '#component/base/Modal';
import {ContactUsModal} from '#component/modal';
import {NumberInputField, PasswordInputField, TextInputField} from '#component/partial';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {sprinkles} from '#style/sprinkles';
import {buildUrl} from '#util/url';
import {
  birthSexValidator,
  emailValidator,
  handleValidationError,
  minimumAgeValidator,
  passwordValidator,
  phoneValidator,
  stringValidator,
} from '#util/validator';
import {ValidationError} from '#util/validator/handleValidationError';

import {CreateAccountFormProps, FieldValues, SubmitHandler} from './CreateAccountForm.types';

const STORE_URL = process.env.NEXT_PUBLIC_STORE_URL;

const CreateAccountFormConfig = {
  defaultValues: {
    // Co-erce type to allow for proper value validation
    birthSex: '' as BirthSex,
    firstName: '',
    lastName: '',
    birthday: '',
    email: '',
    password: '',
    phoneNumber: '',
    acceptTerms: false,
    form: '',
  },
  mode: 'onBlur',
  resolver: yupResolver(
    object({
      acceptTerms: boolean().oneOf([true], 'Please read and agree to the policies'),
      birthSex: birthSexValidator,
      birthday: minimumAgeValidator.required('Please enter your birthday'),
      email: emailValidator,
      firstName: stringValidator.required('Please enter your first name'),
      lastName: stringValidator.required('Please enter your last name'),
      password: passwordValidator,
      phoneNumber: phoneValidator.required('Please enter your phone number'),
    }),
  ),
} as const;

export const CreateAccountForm = ({
  className,
  email,
  firstName,
  lastName,
  returnUrl,
  emailEnabled,
  hideLoginRedirectFooter = false,
  onSubmit,
  onError,
}: CreateAccountFormProps) => {
  const [hadErrors, setHadErrors] = useState(false);
  const {track} = useAnalytics();
  const {
    clearErrors,
    formState: {errors, isSubmitting},
    handleSubmit,
    register,
    setError,
  } = useForm<FieldValues>({
    ...CreateAccountFormConfig,
    defaultValues: {
      ...CreateAccountFormConfig.defaultValues,
      email: email || '',
      firstName: firstName || '',
      lastName: lastName || '',
    },
  });

  const handleCreateAccount: SubmitHandler = async ({
    birthday,
    birthSex,
    email,
    firstName,
    lastName,
    password,
    phoneNumber,
  }) => {
    try {
      await onSubmit({
        birthSex,
        email,
        firstName,
        lastName,
        birthday,
        password,
        phoneNumber,
      });

      track<UserActionEvent>({
        evtName: EventType.UserAction,
        actionName: emailEnabled
          ? 'Account Create Success - Gifted Test'
          : 'Account Create Success',
      });
    } catch (error) {
      handleValidationError<FieldValues>({
        error: error as unknown as ValidationError<FieldValues>,
        errorMessages: {
          phoneNumber: {INVALID: 'Please enter a valid phone number'},
          birthday: {INVALID: 'Please enter a valid birthdate'},
          email: {DUPLICATE: 'An account with this email already exists'},
          form: 'Unable to create an account. Please check the entered data and try again.',
          rateLimit: [
            "We're experiencing an unusually high volume of traffic at the moment,",
            'and were unable to create your account.',
            'We apologize for any inconvenience this may cause and thank you for your patience and understanding.',
            'Please try again in a few minutes.',
          ].join(' '),
          password: {TOO_SHORT: 'Passwords must be at least 8 digits long'},
        },
        setError,
      });

      onError?.(error as any as Error);
    }
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0 && !hadErrors) {
      setHadErrors(true);
    }
  }, [hadErrors, errors]);

  return (
    <Form
      className={className}
      clearErrors={clearErrors}
      errors={errors}
      onSubmit={handleSubmit(handleCreateAccount)}>
      <div className={sprinkles({flex: 'flex', direction: 'col', gap: 32})}>
        <TextInputField errors={errors} label="first name" {...register('firstName')} />
        <TextInputField errors={errors} label="last name" {...register('lastName')} />
        <TextInputField
          errors={errors}
          label="email"
          defaultValue={email || ''}
          disabled={!emailEnabled}
          {...register('email')}
          message={() => {
            if (emailEnabled) return null;
            return (
              <Text as="span" color="dustyGray">
                Please{' '}
                <Link href={process.env.NEXT_PUBLIC_CONTACT_URL} target="_blank">
                  contact us
                </Link>{' '}
                if you’d like to use a different email address from your order.
              </Text>
            );
          }}
        />

        <PasswordInputField
          errors={errors}
          label="password"
          labelRightContent="8 characters or longer"
          {...register('password')}
        />

        <NumberInputField
          errors={errors}
          label="Phone Number"
          mask="999-999-9999"
          maskPlaceholder="xxx-xxx-xxxx"
          placeholder="xxx-xxx-xxxx"
          type="phone"
          {...register('phoneNumber')}
        />

        <NumberInputField
          errors={errors}
          label="Birthday"
          labelRightContent="Must be 18 or older"
          labelBottomContent="DOB ensures accurate results"
          mask="99/99/9999"
          maskPlaceholder="mm/dd/yyyy"
          placeholder="mm/dd/yyyy"
          {...register('birthday')}
        />

        <div className={sprinkles({flex: 'flex', dir: 'col', gap: 12})}>
          <TextInputField
            errors={errors}
            label="Birth sex"
            placeholder=" "
            type="select"
            options={[
              {value: BirthSex.Unknown, label: 'Prefer not to say'},
              {value: BirthSex.Female, label: 'Female'},
              {value: BirthSex.Male, label: 'Male'},
            ]}
            {...register('birthSex')}
          />

          <Text color="dustyGray" variant="caption">
            This helps us improve data accuracy.
          </Text>
        </div>

        <div className={sprinkles({display: 'flex', dir: 'col', gap: 24, pT: 16})}>
          <div className={sprinkles({display: 'flex', dir: 'col'})}>
            <label className={sprinkles({flex: 'flex', gap: 8, items: 'start'})}>
              <Checkbox {...register('acceptTerms')} />

              <Text color="doveGray">
                {'I have read and agree to the '}
                <Text
                  as="a"
                  color="eggplant"
                  href={`${STORE_URL}/policies/terms-of-service`}
                  rel="noreferrer"
                  target="_blank">
                  Terms of Service
                </Text>
                {' and '}
                <Text
                  as="a"
                  color="eggplant"
                  href={`${STORE_URL}/policies/privacy-policy`}
                  rel="noreferrer"
                  target="_blank">
                  Privacy Policy
                </Text>
              </Text>
            </label>
            {errors.acceptTerms && <Text color="solidRed">{errors.acceptTerms.message}</Text>}
          </div>
        </div>
      </div>

      <div className={sprinkles({flex: 'flex', direction: 'col', gap: 24, placeContent: 'center'})}>
        <Button disabled={isSubmitting} icon="ArrowRight" type="submit" loading={isSubmitting}>
          Create Account
        </Button>

        <div className={sprinkles({flex: 'flex', dir: 'col', gap: 8, items: 'center'})}>
          {!hideLoginRedirectFooter && (
            <Text as="p" className={sprinkles({flex: 'flex', gap: 8})} color="dustyGray">
              Already have an account?
              <NextLink
                href={buildUrl({path: '/signin', queries: {return_to: returnUrl}})}
                passHref>
                <Link>Log in</Link>
              </NextLink>
            </Text>
          )}

          {hadErrors && (
            <Text as="p" className={sprinkles({flex: 'flex', gap: 8})} color="dustyGray">
              Having issues?
              <ModalRoot>
                <Link as={ModalTrigger}>Contact Us</Link>
                <ContactUsModal />
              </ModalRoot>
            </Text>
          )}
        </div>
      </div>
    </Form>
  );
};
